import { useState, useEffect, useRef } from 'react';
import type { BlockViewProps as PloneBlockViewProps } from '@plone/types';
import { useSelector } from 'react-redux';
import { FormState } from '@fhnw/types/formState';
import clsx from 'clsx';
import SafeHtmlWithScripts from './SafeHtmlWithScripts';
interface HTMLBlockViewProps extends PloneBlockViewProps {
  isEditMode?: boolean;
}

const HTMLBlockView = (props: HTMLBlockViewProps) => {
  const { data, style, className, isEditMode = false } = props;
  const token = useSelector<FormState, string>(
    (state) => state.userSession.token,
  );
  const htmlContent = data?.href?.[0]?.snippet ?? null;
  const useIframe = data?.iframe_view ?? false;
  const [iframeHeight, setIframeHeight] = useState<string | number>('200px');
  const iframeRef = useRef<HTMLIFrameElement>(null);

  useEffect(() => {
    if (!useIframe) {
      return;
    }
    const handleIframeMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) {
        return;
      }
      if (event.source !== iframeRef.current?.contentWindow) {
        return;
      }
      const messageData = event.data;
      if (messageData && messageData.type === 'htmlBlockResize') {
        const height = messageData.height;
        if (typeof height === 'number' && height >= 0) {
          const newHeight = height + 10; // buffer space
          const currentHeightNumeric =
            typeof iframeHeight === 'string'
              ? parseFloat(iframeHeight)
              : iframeHeight;
          if (newHeight !== currentHeightNumeric) {
            setIframeHeight(newHeight);
          }
        }
      }
    };
    window.addEventListener('message', handleIframeMessage);
    return () => {
      window.removeEventListener('message', handleIframeMessage);
    };
  }, [useIframe, iframeHeight]);

  // Iframe View
  if (useIframe && htmlContent) {
    return (
      <div
        className={clsx('html-block-iframe-wrapper', className)}
        style={{ ...style }}
      >
        <iframe
          ref={iframeRef}
          data-content={htmlContent}
          src="/html_block_iframe.html"
          title="HTML Content"
          className="html-block-iframe"
          style={{
            height:
              typeof iframeHeight === 'number'
                ? `${iframeHeight}px`
                : iframeHeight,
          }}
        />
        {isEditMode && <div className="html-block-overlay"></div>}
      </div>
    );
  }

  // Direct Render
  if (htmlContent) {
    return (
      <SafeHtmlWithScripts
        className={className}
        style={style}
        htmlContent={htmlContent}
      />
    );
  }

  // Placeholder when logged in
  if (token) {
    return (
      <div className={clsx('html-block-placeholder', className)} style={style}>
        Select an HTML snippet in the sidebar. <br /> The content will appear
        here after saving.
      </div>
    );
  }

  // Otherwise, return null
  return null;
};

export default HTMLBlockView;
